import { Link } from '@tanstack/react-router';
import { CircleAlertIcon } from 'lucide-react';

import type { AttentionConcentration } from '@/features/tasks';
import { boardStateLink } from '../links';

export interface ConcentrationCalloutProps {
  workspaceId: string;
  concentration: AttentionConcentration | null;
}

/**
 * "Most of what is broken is in one place."
 *
 * The list below can show five rows that are four copies of one problem — same
 * board, same step, byte-identical error — and leave the reader to notice it by
 * comparing five near-identical lines. That is exactly the kind of pattern a
 * person is bad at and a reduction is good at, and noticing it is what decides
 * whether they open five tasks or one board.
 *
 * This is the only element on the page that says something the rows do not
 * already say. It is still not new information: every word comes from rows the
 * derivation has already produced, so it cannot disagree with the list beneath
 * it, and it reads no history and invents no threshold.
 *
 * Rendered only when the derivation found a strict majority — silence is the
 * common case and the right one. A callout that fired on any two matching rows
 * would appear on nearly every workspace and would stop being read.
 */
export function ConcentrationCallout({
  workspaceId,
  concentration,
}: ConcentrationCalloutProps) {
  if (!concentration) return null;

  const { count, total, workflowName, stepLabel, sameFailure, workflowKey } =
    concentration;

  return (
    <aside
      className="border-board-state-failed/25 bg-board-state-failed/5 flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5"
      aria-label="What most of the breakage has in common"
    >
      <CircleAlertIcon
        aria-hidden="true"
        className="text-board-state-failed mt-px size-4 shrink-0"
        strokeWidth={1.7}
      />
      <p className="text-foreground text-xs leading-relaxed">
        <span className="font-medium">
          {count} of {total} broken tasks
        </span>{' '}
        are on <span className="font-medium">{workflowName}</span>
        {/* An orphan has no step, so the group is "on this board" and nothing
            finer. Naming a step it does not stand on would be the one kind of
            precision this page must never fake. */}
        {stepLabel ? (
          <>
            , all on <span className="font-medium">{stepLabel}</span>
          </>
        ) : null}
        {sameFailure ? ', all with the same error' : null}.{' '}
        {concentration.hasBoard ? (
          <Link
            {...boardStateLink(workspaceId, workflowKey, 'failed')}
            className="text-foreground underline underline-offset-4"
          >
            Open that board
          </Link>
        ) : null}
      </p>
    </aside>
  );
}
